import { FC, useMemo, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { DashboardViewProps } from "./dashboard.interfaces";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  InputAdornment,
  Box,
  LinearProgress,
  Chip,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  School as SchoolIcon,
  CheckCircle,
  HourglassEmpty,
  Archive,
  RadioButtonUnchecked,
  RadioButtonChecked,
  EditNote,
  LabelOutlined,
} from "@mui/icons-material";
import MainTitle from "../../components/mainTitle";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import { useDashboardHook } from "../../../hooks/useDashboardHook";
import { useInstituteContext } from "../../../context/institute.context";
import {
  TInstitute,
  TInstituteStatus,
} from "../../../apis/institute/institute.interfaces";
import { useNavigate } from "react-router-dom";
import { pages } from "../../../utils/common";
import { useAuthUserState } from "../../../context/authUser.context";
import { canAccessInstituteLevelPages } from "../../../utils/userTypes";
import {
  formatInstituteStatusLabel,
  instituteStatusChipColor,
} from "../institute/institute.helpers";

const DASHBOARD_STATUS_FILTERS = [
  "all",
  "active",
  "pending",
  "draft",
  "other",
  "archived",
] as const;

type DashboardStatusFilter = (typeof DASHBOARD_STATUS_FILTERS)[number];

const DashboardView: FC<DashboardViewProps> = () => {
  const { data: instituteList, isLoading, error } = useDashboardHook();
  const { selectedInstitute, setSelectedInstitute } = useInstituteContext();
  const { userDetails } = useAuthUserState();
  const { control, watch } = useForm({ defaultValues: { search: "" } });
  const searchQuery = watch("search");
  const [statusFilter, setStatusFilter] =
    useState<DashboardStatusFilter>("all");
  const navigate = useNavigate();

  // Redirect restricted users away from dashboard
  useEffect(() => {
    if (!canAccessInstituteLevelPages(userDetails)) {
      navigate(pages.spocPremise.path, { replace: true });
    }
  }, [userDetails, navigate]);

  const filteredInstitutes = useMemo(() => {
    if (!instituteList?.institutes) return [];

    return instituteList.institutes.filter((institute) => {
      const matchesSearch =
        institute.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        institute.id.toLowerCase().includes(searchQuery.toLowerCase());

      const isSoftDeleted = !!institute.deletedAt;
      const matchesStatus =
        statusFilter === "all"
          ? !isSoftDeleted
          : statusFilter === "archived"
            ? isSoftDeleted || institute.status === "archived"
            : !isSoftDeleted && institute.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [instituteList?.institutes, searchQuery, statusFilter]);

  const getStatusIcon = (status: TInstituteStatus) => {
    switch (status) {
      case "active":
        return <CheckCircle color="success" />;
      case "pending":
        return <HourglassEmpty color="warning" />;
      case "draft":
        return <EditNote color="info" />;
      case "other":
        return <LabelOutlined color="action" />;
      case "archived":
        return <Archive color="disabled" />;
      default:
        return null;
    }
  };

  const getStatusChipColor = (status: TInstituteStatus) =>
    instituteStatusChipColor(status);

  const handleInstituteSelect = (institute: TInstitute) => {
    setSelectedInstitute(institute);
  };

  const quickAccessLinks = [
    { label: "Institute", path: pages.institute.path },
    { label: "SPOC & Premise", path: pages.spocPremise.path },
    { label: "Compliance", path: pages.compliance.path },
    { label: "Academics", path: pages.academics.path },
    { label: "Assessment", path: pages.assessment.path },
    { label: "Branding", path: pages.branding.path },
    { label: "Localization", path: pages.localization.path },
  ];

  // Don't render dashboard for restricted users (redirect should happen, but this is a safety check)
  if (!canAccessInstituteLevelPages(userDetails)) {
    return null;
  }

  if (isLoading) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.dashboard} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.dashboard} />
        <Alert severity="error">
          Failed to load dashboard data. Please try again later.
        </Alert>
      </>
    );
  }

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.dashboard} />

      <Grid container spacing={3} data-testid="dashboard-view">
        <Grid item xs={12}>
          <MainTitle title="Dashboard" />
        </Grid>

        {/* Quick Access Links */}
        <Grid item xs={12}>
          <Card data-testid="quick-access-card">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Access
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {quickAccessLinks.map((link) => (
                  <Button
                    key={link.path}
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(link.path)}
                    data-testid={`quick-access-${link.label.toLowerCase().replace(/\s+/g, "-")}-button`}
                  >
                    {link.label}
                  </Button>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Configuration Summary */}
        {instituteList?.configurationSummary && (
          <Grid item xs={12}>
            <Card data-testid="configuration-summary-card">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Configuration Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Total Institutes
                    </Typography>
                    <Typography variant="h5">
                      {instituteList.configurationSummary.totalInstitutes}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Active Institutes
                    </Typography>
                    <Typography variant="h5">
                      {instituteList.configurationSummary.activeInstitutes}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Average Completeness
                    </Typography>
                    <Typography variant="h5">
                      {Math.round(
                        instituteList.configurationSummary.averageCompleteness,
                      )}
                      %
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Search and Filter */}
        <Grid item xs={12}>
          <Card data-testid="dashboard-filters-card">
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <ControlledTextField
                    control={control as any}
                    name="search"
                    id="dashboard-search"
                    label="Search institutes"
                    placeholder="Search institutes..."
                    fullWidth
                    isGridItem={false}
                    data-testid="dashboard-search-input"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {DASHBOARD_STATUS_FILTERS.map((status) => (
                      <Chip
                        key={status}
                        label={
                          status === "all"
                            ? "All"
                            : formatInstituteStatusLabel(status)
                        }
                        onClick={() => setStatusFilter(status)}
                        color={statusFilter === status ? "primary" : "default"}
                        variant={
                          statusFilter === status ? "filled" : "outlined"
                        }
                        data-testid={`dashboard-status-filter-${status}`}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Institute List */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Institutes ({filteredInstitutes.length})
          </Typography>
        </Grid>

        {filteredInstitutes.length === 0 ? (
          <Grid item xs={12}>
            <Card data-testid="dashboard-empty-state">
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="200px"
                  gap={2}
                  data-testid="dashboard-empty-content"
                >
                  <SchoolIcon sx={{ fontSize: 64, color: "text.secondary" }} />
                  <Typography variant="h6" color="text.secondary">
                    {instituteList?.institutes?.length === 0
                      ? "No institutes available"
                      : "No institutes match your search criteria"}
                  </Typography>
                  {instituteList?.institutes?.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Contact your administrator to get access to institutes.
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          filteredInstitutes.map((institute) => (
            <Grid item xs={12} sm={6} md={6} lg={4} key={institute.id}>
              <Card
                sx={{
                  cursor: "pointer",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  border:
                    selectedInstitute?.id === institute.id
                      ? "2px solid"
                      : "1px solid",
                  borderColor:
                    selectedInstitute?.id === institute.id
                      ? "primary.main"
                      : "divider",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: 3,
                    transform: "translateY(-2px)",
                  },
                }}
                onClick={() => handleInstituteSelect(institute)}
                data-testid={`institute-card-${institute.id}`}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    pb: 2,
                  }}
                >
                  {/* Radio Button and Status Chip Row */}
                  <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                    {selectedInstitute?.id === institute.id ? (
                      <RadioButtonChecked
                        color="primary"
                        sx={{
                          fontSize: 24,
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <RadioButtonUnchecked
                        sx={{
                          fontSize: 24,
                          flexShrink: 0,
                          color: "action.disabled",
                        }}
                      />
                    )}
                    <Chip
                      label={formatInstituteStatusLabel(institute.status)}
                      color={getStatusChipColor(institute.status)}
                      size="small"
                      sx={{
                        textTransform: "capitalize",
                        fontWeight: 500,
                      }}
                    />
                  </Box>

                  {/* Institute Name and Icon Section */}
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    mb={2.5}
                    minHeight={56}
                  >
                    <SchoolIcon
                      color="primary"
                      sx={{
                        fontSize: 28,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="h6"
                      component="div"
                      sx={{
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                      }}
                    >
                      {institute.name}
                    </Typography>
                    <Box
                      display="flex"
                      alignItems="center"
                      flexShrink={0}
                      ml={1}
                    >
                      {getStatusIcon(institute.status)}
                    </Box>
                  </Box>

                  {/* Progress Section */}
                  <Box>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Configuration Completeness
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ fontSize: "0.875rem" }}
                      >
                        {institute.configurationCompleteness}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={institute.configurationCompleteness}
                      color={
                        institute.configurationCompleteness >= 80
                          ? "success"
                          : institute.configurationCompleteness >= 50
                            ? "warning"
                            : "error"
                      }
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: "action.hover",
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}

        {/* Recent Activity */}
        {instituteList?.recentActivity &&
          instituteList.recentActivity.length > 0 && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Recent Activity
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Card data-testid="recent-activity-card">
                  <CardContent>
                    {instituteList.recentActivity
                      .slice(0, 5)
                      .map((activity) => (
                        <Box
                          key={activity.id}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          py={1}
                          borderBottom="1px solid"
                          borderColor="divider"
                        >
                          <Box>
                            <Typography variant="body1">
                              {activity.description}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {new Date(activity.timestamp).toLocaleString()}
                            </Typography>
                          </Box>
                          <Chip label={activity.type} size="small" />
                        </Box>
                      ))}
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}
      </Grid>
    </>
  );
};

export default DashboardView;
